import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { errorReporter } from '@/services/errorReportingService';
import { SMART_TAGS_FEATURE_ENABLED } from '../config';
import { requestAutoTag } from '../services/autoTagService';

interface UseAutoTagOptions {
  noteId: string | undefined;
  processingJustCompleted: boolean;
  noteHasTags: boolean;
  onTagsApplied?: () => void;
}

/**
 * Triggers auto-tagging for a note after processing completes.
 *
 * Guards:
 * - Smart Tags feature flag enabled
 * - User's plan includes aiTagging (ESSENTIALS+)
 * - Processing just transitioned from active to done
 * - Note doesn't already have tags
 * - Hasn't already fired for this noteId
 *
 * Fire-and-forget — errors are logged silently, never surfaced to the user.
 */
export const useAutoTag = ({ noteId, processingJustCompleted, noteHasTags, onTagsApplied }: UseAutoTagOptions) => {
  const { userToken } = useAuthStore();
  const { features } = useCurrentPlanFeatures();
  const firedForNoteRef = useRef<string | null>(null);

  useEffect(() => {
    if (!SMART_TAGS_FEATURE_ENABLED) return;
    if (!features.aiTagging) return;
    if (!processingJustCompleted) return;
    if (noteHasTags) return;
    if (!noteId || !userToken) return;
    if (firedForNoteRef.current === noteId) return;

    firedForNoteRef.current = noteId;

    let cancelled = false;

    requestAutoTag(userToken, noteId)
      .then(() => {
        if (!cancelled && onTagsApplied) {
          onTagsApplied();
        }
      })
      .catch((error) => {
        errorReporter.expected('Auto-tag request failed', error, {
          feature: 'smartTags',
          operation: 'autoTag',
          noteId,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [noteId, userToken, features.aiTagging, processingJustCompleted, noteHasTags, onTagsApplied]);
};
